import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import _ from "lodash";

import { ReportDatePicker } from "src/components/dateFilter";
import { DATE_RANGES } from "src/constants/reports";
import { daysDifference } from "src/services/reports.service";
import { ReportSummary } from "./ReportSummary";
import { SessionAnalytics } from "./SessionAnalytics";
import { PageAnalytics } from "./PageAnalytics";
import { InteractionAnalytics } from "./InteractionAnalytics";
import { AssetAnalytics } from "./AssetAnalytics";
import { GeographicAnalytics } from "./GeographicAnalytics";
import { MetricsAnalytics } from "./MetricsAnalytics";
import { JourneyAnalytics } from "./JourneyAnalytics";
import { LeadAnalytics } from "../leads/LeadAnalytics";
import { selectDateFilter } from "src/redux/selectors/experiences/leadsAndReports";
import { setDateFilter } from "src/redux/actions/experiences/leadsAndReports";
import { DateRangeType, ReportDateFilter } from "src/types/experiences/leadsAndReports";
import { SLIDE_STORYBOARDS } from "../../constants";
import styles from "./report.module.scss";

interface ReportsTabProps {
  storyboardId: string;
  storyboardType?: string;
}

export const ReportsTab: React.FC<ReportsTabProps> = ({ storyboardId, storyboardType }) => {
  const dispatch = useDispatch();
  const dateFilter = useSelector(selectDateFilter);
  const [interval, setInterval] = useState("day");
  const showJourney = !storyboardType || !SLIDE_STORYBOARDS.includes(storyboardType);

  const handleReportDateChange = useCallback(
    (filter: { reportDate: { type: string; startDate: ReportDateFilter["startDate"]; endDate: ReportDateFilter["endDate"] } }) => {
      const reportDate = filter.reportDate;
      const daysBetween = daysDifference(
        _.get(reportDate, "startDate"),
        _.get(reportDate, "endDate"),
      );
      if (daysBetween > 365) {
        setInterval("week");
      }

      dispatch(
        setDateFilter({
          type: reportDate.type as DateRangeType,
          startDate: reportDate.startDate,
          endDate: reportDate.endDate,
        }),
      );
    },
    [dispatch],
  );

  const handleTimeLineChange = useCallback((value: string) => {
    setInterval(value);
  }, []);

  return (
    <div className={styles.page}>
      <ReportSummary storyboardId={storyboardId} storyboardType={storyboardType} />

      {dateFilter && (
        <ReportDatePicker
          dateRanges={DATE_RANGES}
          value={dateFilter}
          onChange={handleReportDateChange}
          filter={{ interval }}
          handleTimeLineChange={handleTimeLineChange}
          className={styles.datePickerFilter}
          showTimelineFilter={false}
        />
      )}

      <SessionAnalytics storyboardId={storyboardId} storyboardType={storyboardType} />

      <div className={styles.reportsRow}>
        <PageAnalytics storyboardId={storyboardId} storyboardType={storyboardType} />
        <InteractionAnalytics storyboardId={storyboardId} />
      </div>

      <AssetAnalytics storyboardId={storyboardId} />
      <GeographicAnalytics storyboardId={storyboardId} storyboardType={storyboardType} />

      <div className={styles.reportsRow}>
        <MetricsAnalytics storyboardId={storyboardId} metricType="device" tableName="Device Analytics" paginationName="Devices" />
        <MetricsAnalytics storyboardId={storyboardId} metricType="organization" tableName="Account Analytics" paginationName="Accounts" />
      </div>

      <LeadAnalytics storyboardId={storyboardId} />
      {showJourney && <JourneyAnalytics storyboardId={storyboardId} />}

      <div className={styles.reportsRow}>
        <MetricsAnalytics storyboardId={storyboardId} metricType="utm_source" tableName="UTM Source Analytics" paginationName="UTM Sources" />
        <MetricsAnalytics storyboardId={storyboardId} metricType="utm_medium" tableName="UTM Medium Analytics" paginationName="UTM Mediums" />
      </div>
    </div>
  );
};
